import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import {
  BulkReceivePurchaseOrder,
  CreatePurchaseOrder,
  GetAllPurchaseOrdersInput,
  PurchaseOrder,
  PurchaseOrderMatchResult,
  PurchaseOrderService,
  ReceivePurchaseOrder,
  UpdatePurchaseOrder,
} from '@services';

class PurchaseOrderStore {
  items: PurchaseOrder[] = [];
  selectedItem: PurchaseOrder | undefined = undefined;
  loading: boolean = false;
  saving: boolean = false;
  total: number = 0;
  skipCount: number = 0;
  currentPage: number = 1;
  maxResultCount: number = 15;

  // AI invoice receive state
  matchResults: PurchaseOrderMatchResult[] = [];
  matchLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async getAll(request: GetAllPurchaseOrdersInput) {
    this.loading = true;
    try {
      const result = await PurchaseOrderService.getAll(request);
      runInAction(() => {
        this.items = result.items;
        this.total = result.totalCount;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async create(input: CreatePurchaseOrder): Promise<PurchaseOrder> {
    this.saving = true;
    try {
      const result = await PurchaseOrderService.create(input);
      runInAction(() => {
        this.items = [result, ...this.items];
      });
      return result;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async update(input: UpdatePurchaseOrder) {
    this.saving = true;
    try {
      const result = await PurchaseOrderService.update(input);
      runInAction(() => {
        this.items = this.items.map((item) => (item.id === result.id ? result : item));
        if (this.selectedItem?.id === result.id) this.selectedItem = result;
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async delete(id: string) {
    await PurchaseOrderService.delete(id);
    runInAction(() => {
      this.items = this.items.filter((item) => item.id !== id);
    });
  }

  async markOrdered(id: string) {
    await PurchaseOrderService.markOrdered(id);
    runInAction(() => {
      this.items = this.items.map((item) =>
        item.id === id ? { ...item, status: 'Ordered' } : item,
      );
    });
  }

  async receive(input: ReceivePurchaseOrder) {
    await PurchaseOrderService.receive(input);
    runInAction(() => {
      this.items = this.items.map((item) =>
        item.id === input.id
          ? { ...item, status: 'Received', receivedQuantity: input.receivedQuantity, costPrice: input.costPrice ?? item.costPrice }
          : item,
      );
    });
  }

  async cancel(id: string) {
    await PurchaseOrderService.cancel(id);
    runInAction(() => {
      this.items = this.items.map((item) =>
        item.id === id ? { ...item, status: 'Canceled' } : item,
      );
    });
  }

  async matchInvoice(supplierId: string, lines: PurchaseOrderMatchResult['parsedLine'][]) {
    this.matchLoading = true;
    try {
      const results = await PurchaseOrderService.matchInvoice({ supplierId, lines });
      runInAction(() => {
        this.matchResults = results;
      });
    } finally {
      runInAction(() => {
        this.matchLoading = false;
      });
    }
  }

  async bulkReceive(input: BulkReceivePurchaseOrder) {
    this.saving = true;
    try {
      await PurchaseOrderService.bulkReceive(input);
      // Reload received items from the current list to reflect new status.
      const receivedIds = new Set(input.lines.map((l) => l.purchaseOrderId));
      runInAction(() => {
        this.items = this.items.map((item) =>
          receivedIds.has(item.id)
            ? { ...item, status: 'Received' }
            : item,
        );
        this.matchResults = [];
      });
      message.success('Purchase orders received successfully');
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  setSelectedItem(item: PurchaseOrder | undefined) {
    this.selectedItem = item;
  }

  clearStore() {
    this.items = [];
    this.selectedItem = undefined;
    this.matchResults = [];
    this.loading = false;
    this.saving = false;
  }
}

export default PurchaseOrderStore;
